package org.example.project.enginepalettenkonto

import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.LumaticaApiSettings
import org.example.project.enginetabular.loadWithTabularFilters
import org.example.project.service.GlobalManager
import org.example.project.utils.All
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit

object PalettenkontoApi {
    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = false
    }

    suspend fun loadPartner(firmenId: String = ""): List<All> =
        decodeList(LumaticaApiManager.call("palettenkonto/partner/${apiPart(firmenId)}"))

    suspend fun savePartner(rows: List<All>, firmenId: String = ""): List<All> {
        val response = postJson(
            "palettenkonto/partner-save/${apiPart(firmenId)}",
            json.encodeToString(ListSerializer(All.AllNames.serializer()), rows)
        )
        if (!response.ok) return emptyList()
        GlobalManager.markJsonChanged("palettenkonto_partner.json", "palettenkonto")
        return decodeList(response.text().await())
    }

    suspend fun loadKonto(firmenId: String = "", partnerId: String = ""): List<All> =
        decodeList(LumaticaApiManager.call("palettenkonto/konto/${apiPart(firmenId)}/${apiPart(partnerId)}"))

    suspend fun loadAuskunft(firmenId: String = "", monthKey: String = ""): List<All> =
        decodeList(LumaticaApiManager.call("palettenkonto/auskunft/${apiPart(firmenId)}/${apiPart(monthKey)}"))

    suspend fun loadJournal(firmenId: String = ""): List<All> =
        decodeList(LumaticaApiManager.call("palettenkonto/journal/${apiPart(firmenId)}"))

    suspend fun loadJournalAuftraege(firmenId: String = ""): List<All> =
        decodeList(LumaticaApiManager.call("palettenkonto/journal-auftraege/${apiPart(firmenId)}"))

    suspend fun loadDifferenzen(firmenId: String = "", monthKey: String = ""): List<All> =
        decodeList(LumaticaApiManager.call("palettenkonto/differenzen/${apiPart(firmenId)}/${apiPart(monthKey)}"))

    suspend fun loadAbschluesse(firmenId: String = ""): List<All> =
        decodeList(LumaticaApiManager.call("palettenkonto/abschluesse/${apiPart(firmenId)}"))

    suspend fun searchDashboard(
        view: String,
        filters: Map<String, String>,
        context: Map<String, String> = emptyMap()
    ): List<All> =
        decodeList(loadWithTabularFilters("palettenkonto/dashboard-tabular", filters, context + ("view" to view)))

    suspend fun saveJournal(data: All, firmenId: String = ""): All? {
        val response = postJson(
            "palettenkonto/journal-save/${apiPart(firmenId)}",
            json.encodeToString(All.AllNames.serializer(), data)
        )
        if (!response.ok) return null
        val saved = decodeOne(response.text().await())
        if (saved?.id.isNullOrBlank()) return null
        GlobalManager.markJsonChanged("palettenkonto.json", "palettenkonto")
        return saved
    }

    suspend fun uebernehmen(ids: Set<String>, firmenId: String = ""): List<All> =
        postIds("palettenkonto/uebernehmen/${apiPart(firmenId)}", ids)

    suspend fun stornieren(ids: Set<String>, firmenId: String = ""): List<All> =
        postIds("palettenkonto/stornieren/${apiPart(firmenId)}", ids)

    private suspend fun postIds(route: String, ids: Set<String>): List<All> {
        val body = """{"ids":[${ids.joinToString(",") { "\"${it.replace("\\", "\\\\").replace("\"", "\\\"")}\"" }}]}"""
        val response = postJson(route, body)
        if (!response.ok) return emptyList()
        GlobalManager.markJsonChanged("palettenkonto.json", "palettenkonto")
        return decodeList(response.text().await())
    }

    private suspend fun postJson(route: String, body: String) =
        window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/$route",
            RequestInit(
                method = "POST",
                headers = LumaticaApiManager.auditHeaders("application/json"),
                body = body
            )
        ).await()

    private fun decodeList(raw: String): List<All> =
        runCatching { json.decodeFromString(ListSerializer(All.AllNames.serializer()), raw) }.getOrDefault(emptyList())

    private fun decodeOne(raw: String): All? =
        runCatching { json.decodeFromString(All.AllNames.serializer(), raw) }.getOrNull()

    private fun apiPart(value: String): String =
        js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String
}
